import * as THREE from 'three';
import type { Vec3 } from '../../../core/math/vec3';

interface Explosion {
  points: THREE.Points;
  velocities: Float32Array;
  life: number;
  initial: number;
}

// Particle burst — used when the player is overrun. Purely visual; lives in the
// render layer, not authoritative state.
export class Effects {
  private items: Explosion[] = [];

  constructor(private readonly scene: THREE.Scene) {}

  explode(at: Vec3, color = 0xffaa00, count = 120, life = 1, spread = 2): void {
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = at.x;
      positions[i * 3 + 1] = at.y;
      positions[i * 3 + 2] = at.z;
      velocities[i * 3] = (Math.random() - 0.5) * spread;
      velocities[i * 3 + 1] = (Math.random() - 0.5) * spread;
      velocities[i * 3 + 2] = (Math.random() - 0.5) * spread;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({ color, size: 0.2, transparent: true, opacity: 1 });
    const points = new THREE.Points(geo, mat);
    this.scene.add(points);
    this.items.push({ points, velocities, life, initial: life });
  }

  update(dt: number): void {
    const gravity = 0.2;
    for (let i = this.items.length - 1; i >= 0; i--) {
      const ex = this.items[i];
      ex.life -= dt;
      (ex.points.material as THREE.PointsMaterial).opacity = Math.max(ex.life / ex.initial, 0);
      const pos = ex.points.geometry.getAttribute('position') as THREE.BufferAttribute;
      for (let j = 0; j < pos.count; j++) {
        ex.velocities[j * 3 + 1] -= gravity * dt;
        pos.setX(j, pos.getX(j) + ex.velocities[j * 3] * dt);
        pos.setY(j, pos.getY(j) + ex.velocities[j * 3 + 1] * dt);
        pos.setZ(j, pos.getZ(j) + ex.velocities[j * 3 + 2] * dt);
      }
      pos.needsUpdate = true;
      if (ex.life <= 0) {
        this.scene.remove(ex.points);
        ex.points.geometry.dispose();
        (ex.points.material as THREE.Material).dispose();
        this.items.splice(i, 1);
      }
    }
  }

  clear(): void {
    for (const ex of this.items) {
      this.scene.remove(ex.points);
      ex.points.geometry.dispose();
      (ex.points.material as THREE.Material).dispose();
    }
    this.items.length = 0;
  }
}
